// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import submitContactForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SubmitContactForm";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
// import React, { RefObject, useState } from "react"; // Starter Custom Code
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
// import FormActions from "@store/Library/Components/Toolkit/FormActions"; // Store Custom Code
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import constants from "@toolkit/Framework/Common/GoogleAnalytics/constants";
import React, { RefObject, useEffect, useRef, useState } from "react";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { useIsAuthenticated } from "@store/Framework/Store/Context/ContextHooks";
import clearGreifForm from "@store/Framework/Store/Greif/Pages/ContactUs/Handlers/ClearForm";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";

export const topicToHideFieldsArray = ["Get Started with Greif", "Other", ""];
/* Store Custom Code - End */

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    // Store Custom Code
    continueLink = "continueLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        // Store Custom Code
        [fields.continueLink]: string;
    };
}

const mapDispatchToProps = {
    submitContactForm,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    // Store Custom Code
    clearGreifForm,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    // Starter Custom Code
    successBox?: InfosBoxProps;
    // Store Custom Code
    continueFormButton?: ButtonPresentationProps;
    formActions?: FormActionsProps;
}

export const contactFormStyles: ContactFormStyles = {
    container: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
    successModal: { sizeVariant: "small" },
    successBox: {
        type: "success",
    },
};

const styles = contactFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const ContactForm = ({
    id,
    fields,
    submitContactForm,
    clearForm,
    validateReCaptcha,
    // Store Custom Code
    clearGreifForm,
}: Props) => {
    const { emailRecipients, submitButtonText, successMessage } = fields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    /* Starter Custom Code - Start */
    const trackFormStart = ga.use.trackFormStart({ form_name: constants.contactUs.form_name });
    const trackFormSubmit = ga.use.trackFormSubmit({ form_name: constants.contactUs.form_name });
    const canTrackFormStartRef = useRef(true);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const handleFormFocus = () => {
            if (formRef.current?.contains(document.activeElement) && canTrackFormStartRef.current) {
                trackFormStart();
                canTrackFormStartRef.current = false;
            }
        };

        formRef.current?.addEventListener("focus", handleFormFocus, true);

        return () => {
            formRef.current?.removeEventListener("focus", handleFormFocus);
        };
    }, []);
    /* Starter Custom Code - End */

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (hideForm || !hasSelectedTopic) {
            return;
        } // Store Custom Code
        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        submitContactForm({
            emailRecipients,
            onSuccess: () => {
                setIsModalOpen(true);
                setIsSubmitting(false);
                /* Starter Custom Code - Start */
                trackFormSubmit();
                canTrackFormStartRef.current = true;
                /* Starter Custom Code - End */
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                this.onSuccess?.();
            },
        });
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
        clearForm();
        /* Store Custom Code - Start */
        clearGreifForm();
        /* Store Custom Code - End */
    };

    /* Store Custom Code - Start */
    const topic = useAppSelector(state => state.components.contactUsForm.fieldValues["topic"]);
    const isAuthenticated = useIsAuthenticated();
    const selectedTopic = topic || "";
    const hasSelectedTopic = !!selectedTopic;
    const hideForm = !isAuthenticated && hasSelectedTopic && topicToHideFieldsArray.includes(selectedTopic);

    const continueButtonOnClick = () => {
        window.location.href = fields.continueLink;
    };
    /* Store Custom Code - End */

    return (
        <FormWrapper /* Starter Custom Code */>
            <GridContainer {...styles.container}>
                <GridItem {...styles.formGridItem}>
                    <ContactFormContext.Provider value={{ validators }}>
                        <form
                            id="contactForm"
                            onSubmit={handleFormSubmit}
                            noValidate
                            ref={formRef} // Starter Custom Code
                        >
                            <Zone contentId={id} zoneName="Content" />
                            {/* Store Custom Code - Start */}
                            {!hideForm && hasSelectedTopic && (
                                <>
                                    <Zone contentId={id} zoneName="HiddenContent" />
                                    <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                                </>
                            )}
                            {/* Store Custom Code - End */}
                            {/* Starter Custom Code - Start */}
                            {hasSelectedTopic && (
                                <FormActions {...styles.formActions} /* Store Custom Code */>
                                    {/* Store Custom Code - Start */}
                                    {hideForm ? (
                                        <Button
                                            {...styles.continueFormButton}
                                            onClick={continueButtonOnClick}
                                            type="button"
                                        >
                                            {translate("Continue")}
                                        </Button>
                                    ) : (
                                        <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                                            {submitButtonText}
                                        </Button>
                                    )}
                                    {/* Store Custom Code - End */}
                                </FormActions>
                            )}
                            <Modal
                                headline={translate("Message Sent")}
                                contentLabel={successMessage}
                                {...styles.successModal}
                                isOpen={isModalOpen}
                                handleClose={modalCloseHandler}
                            >
                                <InfosBox {...styles.successBox}>{successMessage}</InfosBox>
                                <FormActions>
                                    <Button {...styles.continueButton} onClick={modalCloseHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </FormActions>
                            </Modal>
                            {/* Starter Custom Code - End */}
                        </form>
                    </ContactFormContext.Provider>
                </GridItem>
            </GridContainer>
        </FormWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ContactForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: emailsRegexp,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
            /* Store Custom Code - Start */
            {
                name: fields.continueLink,
                displayName: "Continue Link",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "https://www.greif.com/contact/",
            },
            /* Store Custom Code - End */
        ],
    },
};

export default widgetModule;
